import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || (window.location.origin.replace(/:\d+$/, ':3002') + '/api');

const ALL_SMILEYS = [
  { emoji: '😞', label: 'Pas du tout' },
  { emoji: '😕', label: 'Insuffisant' },
  { emoji: '😐', label: 'Moyen' },
  { emoji: '🙂', label: 'Bien' },
  { emoji: '😄', label: 'Excellent' },
];

function buildSmileys(nb) {
  if (nb >= 5) return ALL_SMILEYS.map((s, i) => ({ ...s, val: i + 1 }));
  const indices = Array.from({ length: nb }, (_, i) => Math.round(i * 4 / (nb - 1)));
  return indices.map((idx, i) => ({ ...ALL_SMILEYS[idx], val: i + 1 }));
}

function QuestionSmiley({ question, value, onChange }) {
  const smileys = buildSmileys(question.options?.nb_smileys || 5);
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <p style={{ fontWeight: 600, marginBottom: '0.8rem', color: '#1e293b', lineHeight: 1.4 }}>
        {question.texte}
      </p>
      <div style={{ display: 'flex', justifyContent: 'space-around', gap: '0.4rem' }}>
        {smileys.map(s => (
          <button key={s.val} onClick={() => onChange(s.val)}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
              padding: '0.5rem 0.3rem', borderRadius: '10px', border: '2px solid',
              borderColor: value === s.val ? '#2563eb' : '#e2e8f0',
              background: value === s.val ? '#eff6ff' : '#f8fafc',
              cursor: 'pointer', flex: 1, transition: 'all 0.15s',
            }}>
            <span style={{ fontSize: '1.8rem' }}>{s.emoji}</span>
            <span style={{ fontSize: '0.6rem', color: value === s.val ? '#2563eb' : '#94a3b8', fontWeight: 600 }}>{s.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function QuestionNote({ question, value, onChange }) {
  const maxNote = question.options?.max_note || 10;
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <p style={{ fontWeight: 600, marginBottom: '0.8rem', color: '#1e293b', lineHeight: 1.4 }}>{question.texte}</p>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        {Array.from({ length: maxNote }, (_, i) => i + 1).map(n => (
          <button key={n} onClick={() => onChange(n)}
            style={{
              width: '44px', height: '44px', borderRadius: '8px', border: '2px solid',
              borderColor: value === n ? '#2563eb' : '#e2e8f0',
              background: value === n ? '#2563eb' : '#f8fafc',
              color: value === n ? '#fff' : '#475569',
              fontWeight: 700, fontSize: '1rem', cursor: 'pointer',
            }}>{n}</button>
        ))}
      </div>
    </div>
  );
}

function QuestionChapitre({ question }) {
  return (
    <div style={{
      margin: '1.2rem 0 0.6rem', padding: '0.6rem 1rem',
      background: '#e0e7ff', borderLeft: '4px solid #6366f1',
      borderRadius: '0 8px 8px 0',
      fontWeight: 700, color: '#3730a3', fontSize: '1rem', lineHeight: 1.4,
    }}>
      {question.texte}
    </div>
  );
}

function QuestionChoix({ question, value, onChange }) {
  const options = question.options || [];
  // Supporte la sélection multiple : value est un tableau
  const selected = Array.isArray(value) ? value : (value ? [value] : []);

  function toggle(opt) {
    const newSelected = selected.includes(opt)
      ? selected.filter(v => v !== opt)
      : [...selected, opt];
    onChange(newSelected.length === 0 ? [] : newSelected);
  }

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <p style={{ fontWeight: 600, marginBottom: '0.8rem', color: '#1e293b', lineHeight: 1.4 }}>{question.texte}</p>
      <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '0.5rem', fontStyle: 'italic' }}>
        Plusieurs réponses possibles
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {options.map((opt, i) => {
          const isSelected = selected.includes(opt);
          return (
            <button key={i} onClick={() => toggle(opt)}
              style={{
                padding: '0.7rem 1rem', borderRadius: '8px', border: '2px solid',
                borderColor: isSelected ? '#2563eb' : '#e2e8f0',
                background: isSelected ? '#eff6ff' : '#f8fafc',
                color: isSelected ? '#2563eb' : '#1e293b',
                textAlign: 'left', cursor: 'pointer', fontWeight: isSelected ? 700 : 400,
                display: 'flex', alignItems: 'center', gap: '0.7rem',
              }}>
              <span style={{
                width: 18, height: 18, borderRadius: 4, border: '2px solid',
                borderColor: isSelected ? '#2563eb' : '#cbd5e1',
                background: isSelected ? '#2563eb' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, transition: 'all 0.15s',
              }}>
                {isSelected && <span style={{ color: '#fff', fontSize: '0.65rem', fontWeight: 900, lineHeight: 1 }}>✓</span>}
              </span>
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function QuestionTexte({ question, value, onChange }) {
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <p style={{ fontWeight: 600, marginBottom: '0.8rem', color: '#1e293b', lineHeight: 1.4 }}>{question.texte}</p>
      <textarea value={value || ''} onChange={e => onChange(e.target.value)}
        rows={3}
        style={{
          width: '100%', padding: '0.7rem', borderRadius: '8px',
          border: '2px solid #e2e8f0', fontSize: '1rem',
          resize: 'vertical', fontFamily: 'inherit',
        }}
        placeholder="Votre réponse..." />
    </div>
  );
}

function QuestionOuiNon({ question, value, onChange }) {
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <p style={{ fontWeight: 600, marginBottom: '0.8rem', color: '#1e293b', lineHeight: 1.4 }}>{question.texte}</p>
      <div style={{ display: 'flex', gap: '1rem' }}>
        {['Oui', 'Non'].map(opt => (
          <button key={opt} onClick={() => onChange(opt)}
            style={{
              flex: 1, padding: '0.7rem', borderRadius: '8px', border: '2px solid',
              borderColor: value === opt ? (opt === 'Oui' ? '#16a34a' : '#dc2626') : '#e2e8f0',
              background: value === opt ? (opt === 'Oui' ? '#f0fdf4' : '#fef2f2') : '#f8fafc',
              color: value === opt ? (opt === 'Oui' ? '#16a34a' : '#dc2626') : '#475569',
              fontWeight: 700, fontSize: '1rem', cursor: 'pointer',
            }}>{opt}</button>
        ))}
      </div>
    </div>
  );
}

// Composant image illustrative (affichée au-dessus de la question)
function QuestionImage({ url }) {
  if (!url) return null;
  return (
    <div style={{ marginBottom: '0.8rem', borderRadius: '10px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
      <img src={`${API_BASE}${url}`} alt="Illustration" style={{ width: '100%', maxHeight: '220px', objectFit: 'contain', background: '#f8fafc' }} />
    </div>
  );
}

// Composant photo comme réponse (stagiaire prend une photo)
function QuestionPhoto({ question, value, onChange, token }) {
  const [preview, setPreview] = useState(value?.preview || null);
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef();

  async function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    // Compress image via canvas before upload
    setUploading(true);
    try {
      const img = new Image();
      const reader = new FileReader();
      reader.onload = async (ev) => {
        img.onload = async () => {
          const canvas = document.createElement('canvas');
          const MAX = 1200;
          let w = img.width, h = img.height;
          if (w > MAX) { h = Math.round(h * MAX / w); w = MAX; }
          if (h > MAX) { w = Math.round(w * MAX / h); h = MAX; }
          canvas.width = w; canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          canvas.toBlob(async (blob) => {
            const fd = new FormData();
            fd.append('photo', blob, 'photo.jpg');
            const r = await fetch(`${API_BASE}/q/${token}/photo/${question.question_id}`, { method: 'POST', body: fd });
            const d = await r.json();
            if (d.ok) {
              setPreview(URL.createObjectURL(blob));
              onChange({ photo_url: d.photo_url, preview: URL.createObjectURL(blob) });
            }
            setUploading(false);
          }, 'image/jpeg', 0.82);
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    } catch { setUploading(false); }
  }

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <p style={{ fontWeight: 600, marginBottom: '0.8rem', color: '#1e293b', lineHeight: 1.4 }}>{question.texte}</p>
      <QuestionImage url={question.image_url} />
      {preview && (
        <div style={{ marginBottom: '0.8rem', borderRadius: '10px', overflow: 'hidden', border: '2px solid #16a34a' }}>
          <img src={preview} alt="Votre photo" style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', background: '#f8fafc' }} />
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleFile} />
      <button onClick={() => inputRef.current?.click()} disabled={uploading}
        style={{
          width: '100%', padding: '0.9rem', borderRadius: '10px',
          background: preview ? '#f0fdf4' : '#f8fafc',
          border: '2px dashed ' + (preview ? '#16a34a' : '#cbd5e1'),
          color: preview ? '#16a34a' : '#64748b',
          fontSize: '1rem', cursor: uploading ? 'not-allowed' : 'pointer', fontWeight: 600,
        }}>
        {uploading ? '⏳ Envoi...' : preview ? '📷 Reprendre la photo' : '📷 Prendre une photo'}
      </button>
    </div>
  );
}


export default function QuestionnairePublic() {
  const { token } = useParams();
  const [etat, setEtat] = useState('chargement'); // chargement | ok | deja_utilise | inactif | erreur | soumis
  const [info, setInfo] = useState(null);
  const [reponses, setReponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [commence, setCommence] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/q/${token}`)
      .then(r => r.json())
      .then(d => {
        if (d.statut === 'ok') {
          setInfo(d);
          setEtat('ok');
        } else if (d.statut === 'emargement') {
          const sidParam = d.stagiaire_id ? `?sid=${encodeURIComponent(d.stagiaire_id)}` : '';
          window.location.replace(`${window.location.origin}/emargement/${d.emargement_token}${sidParam}`);
        } else {
          setMessage(d.message || d.error || 'Erreur inconnue');
          setEtat(d.statut || 'erreur');
        }
      })
      .catch(() => { setMessage('Impossible de contacter le serveur.'); setEtat('erreur'); });
  }, [token]);

  function setReponse(questionId, val) {
    setReponses(r => ({ ...r, [questionId]: val }));
  }

  async function handleSubmit() {
    const questions = info?.questionnaire?.questions || [];
    const defautObligatoire = info?.questionnaire?.reponses_obligatoires !== false;
    const requises = questions.filter(q => {
      if (q.type_reponse === 'texte' || q.type_reponse === 'photo' || q.type_reponse === 'chapitre') return false;
      if (q.optionnel) return false; // compatibilité satisfaction par défaut
      // Per-question: obligatoire explicite, sinon fallback sur questionnaire
      return q.obligatoire !== undefined ? q.obligatoire !== false : defautObligatoire;
    });
    const manquantes = requises.filter(q => {
      const r = reponses[q.question_id];
      if (r === undefined || r === null) return true;
      if (Array.isArray(r) && r.length === 0) return true;
      return false;
    });
    if (manquantes.length > 0) {
      alert(`Merci de répondre à toutes les questions obligatoires (${manquantes.length} restante${manquantes.length > 1 ? 's' : ''}).`);
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_BASE}/q/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reponses }),
      });
      const d = await r.json();
      if (d.ok) { setMessage(d.message); setEtat('soumis'); }
      else { alert('Erreur : ' + (d.error || 'Inconnue')); }
    } catch { alert('Erreur réseau.'); }
    finally { setSubmitting(false); }
  }

  const containerStyle = {
    minHeight: '100vh', background: '#f8fafc',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '1.5rem 1rem',
  };
  const cardStyle = {
    background: '#fff', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
    padding: '1.5rem', width: '100%', maxWidth: '480px',
  };

  if (etat === 'chargement') return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⏳</div>
          Chargement...
        </div>
      </div>
    </div>
  );

  if (etat === 'soumis') return (
    <div style={containerStyle}>
      <div style={{ ...cardStyle, textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
        <h2 style={{ color: '#16a34a', marginBottom: '0.5rem' }}>Merci !</h2>
        <p style={{ color: '#475569' }}>{message}</p>
      </div>
    </div>
  );

  if (etat === 'deja_utilise') return (
    <div style={containerStyle}>
      <div style={{ ...cardStyle, textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔒</div>
        <h2 style={{ color: '#f97316', marginBottom: '0.5rem' }}>QR code déjà utilisé</h2>
        <p style={{ color: '#475569' }}>{message}</p>
      </div>
    </div>
  );

  if (etat === 'inactif') return (
    <div style={containerStyle}>
      <div style={{ ...cardStyle, textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏸️</div>
        <h2 style={{ color: '#64748b', marginBottom: '0.5rem' }}>En attente</h2>
        <p style={{ color: '#475569' }}>{message}</p>
      </div>
    </div>
  );

  if (etat === 'erreur') return (
    <div style={containerStyle}>
      <div style={{ ...cardStyle, textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
        <h2 style={{ color: '#dc2626', marginBottom: '0.5rem' }}>Erreur</h2>
        <p style={{ color: '#475569' }}>{message}</p>
      </div>
    </div>
  );

  const questionnaire = info?.questionnaire;
  const questions = questionnaire?.questions || [];
  const typeLabel = (info?.types || []).find(t => t.val === info?.type_actif)?.label
    || { positionnement: 'Positionnement', evaluation: 'Évaluation', evaluation_formative: 'Évaluation formative', satisfaction: 'Satisfaction' }[info?.type_actif]
    || info?.type_actif;
  const defautObligatoire = questionnaire?.reponses_obligatoires !== false;

  const nomStagiaire = [info?.stagiaire?.civilite, info?.stagiaire?.prenoms, info?.stagiaire?.nom].filter(Boolean).join(' ')
    || (info?.stagiaire?.prenom ? `${info.stagiaire.prenom} ${info.stagiaire.nom || ''}`.trim() : null);

  // ── Écran intro ──────────────────────────────────────────────────────────
  if (!commence) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.4rem' }}>
              {typeLabel}
            </div>
            <h1 style={{ fontSize: '1.25rem', color: '#1e293b', fontWeight: 700, marginBottom: '0.4rem', lineHeight: 1.3 }}>
              {questionnaire?.titre || 'Questionnaire'}
            </h1>
            {nomStagiaire && (
              <div style={{ fontSize: '0.88rem', color: '#64748b', marginBottom: '0.8rem' }}>
                {nomStagiaire}
              </div>
            )}
          </div>

          {questionnaire?.description && (
            <div style={{
              background: '#f1f5f9', borderRadius: '10px', padding: '1rem',
              marginBottom: '1.5rem', color: '#334155', fontSize: '0.92rem',
              lineHeight: 1.6, borderLeft: '4px solid #2563eb',
            }}>
              {questionnaire.description}
            </div>
          )}

          <div style={{ fontSize: '0.82rem', color: '#94a3b8', textAlign: 'center', marginBottom: '1.2rem' }}>
            {(() => { const nb = questions.filter(q => q.type_reponse !== 'chapitre').length; return nb + ' question' + (nb > 1 ? 's' : ''); })()}
            {defautObligatoire && <span> · réponses obligatoires</span>}
          </div>

          <button onClick={() => setCommence(true)}
            style={{
              width: '100%', padding: '1rem', borderRadius: '12px',
              background: '#2563eb', color: '#fff', fontWeight: 700,
              fontSize: '1.05rem', border: 'none', cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(37,99,235,0.35)',
            }}>
            Commencer
          </button>
        </div>
      </div>
    );
  }

  // ── Écran questionnaire ──────────────────────────────────────────────────
  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        {/* En-tête */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.3rem' }}>
            {typeLabel}
          </div>
          <h1 style={{ fontSize: '1.2rem', color: '#1e293b', fontWeight: 700, marginBottom: '0.3rem' }}>
            {questionnaire?.titre || 'Questionnaire'}
          </h1>
          {nomStagiaire && (
            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
              {nomStagiaire}
            </div>
          )}
          {defautObligatoire && (
            <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '0.3rem' }}>
              <span style={{ color: '#dc2626' }}>*</span> champs obligatoires
            </div>
          )}
        </div>

        {/* Questions avec sections */}
        <div>
          {(() => {
            const sections = questionnaire?.sections || [];
            let lastSection = -2;
            return questions.map(q => {
              if (!q || !q.question_id) return null;
              const val = reponses[q.question_id];
              const sectionIdx = q.section ?? null;
              const showSection = sectionIdx !== null && sectionIdx !== lastSection && sections[sectionIdx];
              if (showSection) lastSection = sectionIdx;

              // Déterminer si la question est obligatoire
              const estObligatoire = q.type_reponse === 'chapitre' ? false : (q.optionnel ? false
                : (q.obligatoire !== undefined ? q.obligatoire !== false : defautObligatoire));
              const estTextePossible = q.type_reponse === 'texte' || q.type_reponse === 'photo' || q.type_reponse === 'chapitre';

              // Injecter indicateur obligatoire dans l'objet question
              const qAvecLabel = {
                ...q,
                texte: estObligatoire && !estTextePossible
                  ? q.texte  // indicateur affiché séparément
                  : q.texte,
              };

              const Img = q.image_url ? () => <QuestionImage url={q.image_url} /> : () => null;
              const renderQuestion = () => {
                if (q.type_reponse === 'chapitre') return <QuestionChapitre question={q} />;
                if (q.type_reponse === 'photo')   return <QuestionPhoto  question={q} value={val} onChange={v => setReponse(q.question_id, v)} token={token} />;
                if (q.type_reponse === 'smiley')  return <><Img /><QuestionSmiley question={q} value={val} onChange={v => setReponse(q.question_id, v)} /></>;
                if (q.type_reponse === 'note')    return <><Img /><QuestionNote   question={q} value={val} onChange={v => setReponse(q.question_id, v)} /></>;
                if (q.type_reponse === 'choix')   return <><Img /><QuestionChoix  question={q} value={val} onChange={v => setReponse(q.question_id, v)} /></>;
                if (q.type_reponse === 'oui_non') return <><Img /><QuestionOuiNon question={q} value={val} onChange={v => setReponse(q.question_id, v)} /></>;
                return <><Img /><QuestionTexte question={q} value={val} onChange={v => setReponse(q.question_id, v)} /></>;
              };

              return (
                <React.Fragment key={q.question_id}>
                  {showSection && (
                    <div style={{
                      margin: '1rem 0 0.8rem', padding: '0.4rem 0.8rem',
                      background: '#f1f5f9', borderLeft: '3px solid #2563eb',
                      borderRadius: '0 6px 6px 0', fontSize: '0.82rem',
                      fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.5px',
                    }}>
                      {sections[sectionIdx]}
                    </div>
                  )}
                  {q.optionnel && q.note && (
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: '0.3rem', fontStyle: 'italic' }}>
                      {q.note}
                    </div>
                  )}
                  {/* Badge obligatoire/optionnel */}
                  {estObligatoire && !estTextePossible && (
                    <div style={{ fontSize: '0.68rem', color: '#dc2626', marginBottom: '0.15rem', fontWeight: 600 }}>* obligatoire</div>
                  )}
                  {!estObligatoire && !q.optionnel && !estTextePossible && defautObligatoire && (
                    <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginBottom: '0.15rem', fontStyle: 'italic' }}>optionnel</div>
                  )}
                  {renderQuestion()}
                </React.Fragment>
              );
            });
          })()}
        </div>

        {/* Bouton soumettre */}
        <button onClick={handleSubmit} disabled={submitting}
          style={{
            width: '100%', padding: '1rem', borderRadius: '12px',
            background: submitting ? '#94a3b8' : '#2563eb',
            color: '#fff', fontWeight: 700, fontSize: '1rem',
            border: 'none', cursor: submitting ? 'not-allowed' : 'pointer',
            marginTop: '0.5rem',
          }}>
          {submitting ? 'Envoi en cours...' : 'Valider mes réponses'}
        </button>
      </div>
    </div>
  );
}
